import Assets from "./assets";
import UI from "./ui";
import Utils from "./utils";

export default class ImportTool {
    /**
     * Analyze the files to import
     * @param {String[]} pfn Path file names
     * @param {Number} maxAssetSize Maximum size in bytes for assets
     * @param {Object} pb Progress bars
     * @param {ProgressBar} pb.overall Overall progress bar
     * @param {ProgressBar} pb.task Task progress bar
     * @param {ProgressBar} pb.upload File upload progress bar
     * @returns {Object} Details about the files analyzed
     */
    async _stepAnalyzeFiles(pfn, maxAssetSize, pb) {
        this.files = pfn;

        // Load ZIP file
        this.zip = null;
        let files = this.files;
        if (this.files.length === 1 && !this.files[0].webkitRelativePath) {
            console.log("Reading ZIP file");
            try {
                this.zip = await JSZip.loadAsync(this.files[0]);
                files = Object.values(this.zip.files);
            } catch (err) {
                console.error(err);
                return {
                    error: t("Unable to analyze content: {{- reason}}", {
                        reason: t("Error reading ZIP file contents."),
                    }),
                };
            }
        }
        console.log("Analyzing files");

        // Analyze files
        this.modules = [];
        this.systems = [];
        this.worlds = [];
        this.assets = [];
        this.table = null;
        this.setFVTTVersion = null;
        this.failedInstallReasons = {};
        let worldsHighestCoreVersion = 0;

        const foundryvttPrefix = files.every((file) => {
            const path = this.zip ? file.name : file.webkitRelativePath;
            return Utils.prefixPath(Utils.dirname(path), path, "FoundryVTT");
        });
        let dataPrefix = true;
        /* First find all modules/systems/worlds */
        for (let file of files) {
            file.path = this.zip ? file.name : file.webkitRelativePath;
            if (foundryvttPrefix) {
                file.path = file.path.slice("FoundryVTT/".length);
            }

            file.dirname = Utils.dirname(file.path);
            file.basename = this.zip ? Utils.basename(file.name) : file.name;
            Object.defineProperty(file, "blob", {
                get() {
                    if (this instanceof Blob) {
                        return this;
                    }
                    if (!this._blob) {
                        this._blob = this.async("blob");
                    }
                    return this._blob;
                },
                configurable: true,
            });

            if (!Utils.prefixPath(file.dirname, file.path, "Data")) {
                dataPrefix = false;
            }
            if (file.basename === "module.json") {
                const json = await Utils.blobToJson(await file.blob);
                this.modules.push({ manifest: file, json, path: file.dirname, files: [], error: "" });
            } else if (file.basename === "system.json") {
                const json = await Utils.blobToJson(await file.blob);
                this.systems.push({ manifest: file, json, path: file.dirname, files: [], error: "" });
            } else if (file.basename === "world.json") {
                const json = await Utils.blobToJson(await file.blob);
                this.worlds.push({ manifest: file, json, path: file.dirname, files: [], error: "" });
                // Find the highest core version used by the worlds
                if (json.coreVersion && Utils.compareFoundryVersions(json.coreVersion, worldsHighestCoreVersion) > 0) {
                    worldsHighestCoreVersion = String(json.coreVersion); // Some versions used a float
                }
            }
        }
        /* Then find all the assets and the files for those packages */
        for (let file of files) {
            if (file.dir) {
                continue;
            }
            const modules = this.modules.filter((m) => Utils.prefixPath(file.dirname, file.path, m.path));
            if (modules.length > 1) {
                modules.forEach((m) => (m.error = t("Nested modules are not supported.")));
                continue;
            } else if (modules.length === 1) {
                const module = modules[0];
                if (module.isCompendium === undefined) {
                    if (module.json) {
                        const scripts = module.json.scripts || [];
                        const esmodules = module.json.esmodules || [];
                        const styles = module.json.styles || [];
                        const packs = module.json.packs || [];
                        module.isCompendium =
                            packs.length > 0 && scripts.length === 0 && esmodules.length === 0 && styles.length === 0;
                    } else {
                        module.isCompendium = false;
                    }
                }
                const media = Assets.MEDIA_EXTENSIONS.includes(Utils.fileext(file.basename.toLowerCase()));
                if (media && module.json) {
                    let path = file.path;
                    const pkgName = module.json.id || module.json.name;
                    const relativePath = module.path === "" ? path : path.slice(module.path.length + 1);
                    path = `modules/${pkgName}/${relativePath}`;
                    if (module.assets === undefined) {
                        module.assets = [];
                    }
                    module.assets.push({
                        file,
                        path,
                    });
                } else {
                    module.files.push(file);
                }
                continue;
            }
            const systems = this.systems.filter((s) => Utils.prefixPath(file.dirname, file.path, s.path));
            if (systems.length > 1) {
                systems.forEach((s) => (s.error = t("Nested systems are not supported.")));
                continue;
            } else if (systems.length === 1) {
                const system = systems[0];
                const media = Assets.MEDIA_EXTENSIONS.includes(Utils.fileext(file.basename.toLowerCase()));
                let path = file.path;
                if (media && system.json) {
                    const pkgName = system.json.name || system.json.id;
                    const relativePath = system.path === "" ? path : path.slice(system.path.length + 1);
                    path = `systems/${pkgName}/${relativePath}`;
                    if (system.assets === undefined) {
                        system.assets = [];
                    }
                    system.assets.push({
                        file,
                        path,
                    });
                } else {
                    systems[0].files.push(file);
                }
                continue;
            }
            const worlds = this.worlds.filter((w) => Utils.prefixPath(file.dirname, file.path, w.path));
            if (worlds.length > 1) {
                worlds.forEach((w) => (w.error = t("Nested worlds are not supported.")));
                continue;
            } else if (worlds.length === 1) {
                const world = worlds[0];
                // const relativePath = world.path === "" ? file.path : file.path.slice(world.path.length + 1);
                const media = Assets.MEDIA_EXTENSIONS.includes(Utils.fileext(file.basename.toLowerCase()));
                let path = file.path;
                if (world.json) {
                    const pkgName = world.json.name || world.json.id;
                    const relativePath = world.path === "" ? path : path.slice(world.path.length + 1);
                    path = `worlds/${pkgName}/${relativePath}`;
                }
                if (media) {
                    this.assets.push({
                        world: world,
                        file: file,
                        media,
                        path,
                        oversize: this._getFileSize(file) > maxAssetSize,
                    });
                } else {
                    worlds[0].files.push(file);
                }
                continue;
            } else {
                const media = Assets.MEDIA_EXTENSIONS.includes(Utils.fileext(file.basename.toLowerCase()));
                // If importing Data/ folder or FoundryVTT/ then skip the Data prefix in the path
                const path =
                    (foundryvttPrefix || dataPrefix) && file.path.startsWith("Data/")
                        ? file.path.slice("Data/".length)
                        : file.path;
                this.assets.push({
                    world: null,
                    file,
                    media,
                    path,
                    oversize: this._getFileSize(file) > maxAssetSize,
                });
            }
        }

        const assetsToHash = this.assets.filter((a) => a.media);
        pb.setProgress(0, 0);
        pb.setMax(this.assets.length);
        pb.setLabel(t("ASSETS.assets", { count: assetsToHash.length }));
        // Now hash all the assets
        for (let i = 0; i < assetsToHash.length; i++) {
            assetsToHash[i].hash = await Utils.etagFromFile(await assetsToHash[i].file.blob);
            pb.setProgress(i + 1);
        }

        const stats = { nonMedia: {}, assetsOrigin: {} };
        // console.log("Done analyzing packages", this.modules, this.systems, this.worlds, this.assets);
        if (this.worlds.length) {
            const installedWorlds = await Utils.api("data/worlds");
            if (installedWorlds.length === 0) {
                this.table = await Utils.api("game/table?listVersions=1");
                if (this.table && this.table.availableVersions) {
                    const availableVersions = this.table.availableVersions;
                    const version = Object.entries(availableVersions.versions).find(
                        ([_name, numeric]) => numeric == worldsHighestCoreVersion
                    );
                    if (version) {
                        // Set the FVTT version to the appropriate matching version
                        this.setFVTTVersion = version[0];
                    }
                }
            }
            const worldAssets = this.assets.filter((a) => a.world);
            if (worldAssets.length > 0) {
                const nonMedia = worldAssets.filter((a) => !a.media);
                stats.nonMedia.worlds = nonMedia.length;
            }
        }

        if (this.modules.length) {
            const compendiums = this.modules.filter((m) => m.isCompendium);
            stats.compendiums = compendiums.length;
        }
        if (this.assets.length) {
            const worldAssets = this.assets.filter((a) => a.world);
            const nonMedia = this.assets.filter((a) => !a.media);
            stats.assetsOrigin.worlds = worldAssets.length;
            stats.nonMedia.assets = nonMedia.length;
        }
        return {
            worlds: this.worlds,
            systems: this.systems,
            modules: this.modules,
            assets: this.assets,
            stats,
            FVTTVersion: this.setFVTTVersion,
        };
    }

    /**
     * Files importer
     * @param {Object} selection Files to import
     * @param {Array} selection.worlds  Worlds to import
     * @param {Array} selection.systems Systems to import
     * @param {Array} selection.modules Modules to import
     * @param {Array} selection.assets  Loose assets to import (non world/system/module bound)
     * @param {Boolean} useBazaar Install from the Bazaar if the package is found
     * @param {Object} pb UI progress bars
     */
    async _stepImportData(selection, useBazaar, pb) {
        // Create body
        const mediaAssets = this.assets.filter((a) => a.media);

        // TODO: Check quotas before uploading
        // TODO: Check for errors in worlds/systems/modules and don't import if there is an error.
        const results = {};
        for (const type of ["assets", "systems", "modules", "worlds"]) {
            let packages = selection[type];
            if (packages.length === 0) {
                continue;
            }
            const importLabel = t("Importing {{type}}", { type: Utils.capitalize(type) });
            pb.overall.setLabel(importLabel);
            const singularType = type[0].toUpperCase() + type.slice(1, -1);
            this.missingAssets = {};
            if (type === "assets") {
                results[type] = { successes: 0, failures: 0 };
                packages = mediaAssets;
                if (packages.length > 0) {
                    pb.task.setLabel(t("Retrieving Asset Library content…"));
                    this.remoteAssets = await Assets.getFiles();
                }
                const response = await this._importAssets(packages, pb.task, (s, f) => {
                    results[type].successes = s;
                    results[type].failures = f;
                    // pb.task always has a max of 100, so we need to set the success/failure amounts in percentage
                    const successPercent = Math.round((100 * s) / packages.length);
                    const failurePercent = Math.round((100 * f) / packages.length);
                    pb.task.setProgress(successPercent, failurePercent);
                });
                const success = !response.error;
                pb.task.setLabel(
                    success
                        ? t(`Files uploaded Successfully`)
                        : t(`Error uploading files: {{- error}}`, { error: response.error })
                );
                const errors = response.results.filter((r) => r.error).map((r) => ({ path: r.path, error: r.error }));
                if (errors.length > 0) {
                    console.warn("Errors uploading assets:", errors);
                }
                console.log("Done with", type);
                pb.overall.successes += 1;
                // Skip the rest of the loop
                continue;
            } else if (type === "worlds") {
                pb.task.setLabel(t("Retrieving asset list for world data migration…"));
                this.remoteAssets = await Assets.getFiles();

                if (this.setFVTTVersion) {
                    pb.task.setLabel(
                        t(`Configuring Foundry VTT server to {{version}}…`, { version: this.setFVTTVersion })
                    );
                    // Let's set the version to the closest channel (recommended or latest) and
                    // only set the lastVersion to the one the user has last used
                    const { availableVersions } = this.table;
                    // Support either recommended or latest channel, always assume latest is >= recommended
                    // If the version is > recommended, then assume it's available in latest
                    const recommended = availableVersions.versions[availableVersions.channels.recommended];
                    const target = availableVersions.versions[this.setFVTTVersion];
                    const channel = Utils.compareFoundryVersions(recommended, target) >= 0 ? "recommended" : "latest";
                    // Don't set it needlessly
                    await Utils.api("game/configure", {
                        game: this.table.table.slug,
                        version: channel,
                        lastVersion: this.setFVTTVersion,
                    });
                }
            }

            let successes = 0;
            let failures = 0;
            let installedFromBazaar = 0;
            for (const [index, pkg] of packages.entries()) {
                const name = pkg.json ? pkg.json.title || pkg.json.id || pkg.json.name : null;
                this.currentPackage = name;
                const pkgId = pkg.json ? pkg.json.id || pkg.json.name : null;
                // We reuse the string set in the vue component for `<i18n>` sinc
                const typeAndLabel = t("IMPORT_WIZARD.TypeAndLabel", "{0}: {1}")
                    .replace("{0}", "{{type}}")
                    .replace("{1}", "{{detail}}");
                pb.overall.setLabel(
                    t(typeAndLabel, {
                        type: importLabel,
                        detail: name || `${index} / ${packages.length}`,
                        interpolation: { escapeValue: false },
                    })
                );
                let success = false;
                let zip_file = null;
                if (type === "worlds") {
                    pb.task.setLabel(t("Migrating World Data"));
                    zip_file = await this.migrateWorldData(pkg, pb.task);
                } else {
                    if (pkg.json && useBazaar) {
                        success = await this.installPackage(type, pkgId, pkg.json.version);
                    }
                    if (success) {
                        installedFromBazaar += 1;
                        pb.task.setProgress(100);
                    } else if (pkgId !== "plutonium") {
                        if (type === "modules" || type === "systems") {
                            if (pkg.assets && pkg.assets.length > 0) {
                                pb.task.setLabel(
                                    t(`Importing assets for custom package {{name}}.`, {
                                        name: pkgId,
                                    })
                                );
                                pb.upload.reset(pkg.assets.length);
                                const response = await this._importAssets(pkg.assets, pb.upload, async (s, f) => {
                                    pb.upload.setProgress(s, f);
                                });
                                pb.task.setLabel(
                                    response.error
                                        ? t("Error uploading package assets: {{- error}}", { error: response.error })
                                        : t("Package assets uploaded successfully")
                                );
                                const errors = response.results
                                    .filter((r) => r.error)
                                    .map((r) => ({ path: r.path, error: r.error }));
                                if (errors.length > 0) {
                                    console.warn(t("Errors uploading assets:"), errors);
                                }
                                // Update remote assets list for migration
                                this.remoteAssets = await Assets.getFiles();
                            }
                            pb.task.setLabel(t("Migrating package data"));
                            zip_file = await this.migratePackageData(pkg, pb.task);
                        } else {
                            zip_file = new JSZip();
                            if (this.zip) {
                                zip_file.files = pkg.files.reduce((files, entry) => {
                                    files[entry.name] = entry;
                                    return files;
                                }, {});
                            } else {
                                pkg.files.forEach((file) => zip_file.file(file.path, file));
                            }
                        }
                    }
                }
                if (zip_file) {
                    pb.task.setLabel(t("Generating {{singularType}}", { singularType }));
                    const blob = await zip_file.generateAsync(
                        {
                            type: "blob",
                            compression: "DEFLATE",
                            streamFiles: true,
                            compressionOptions: { level: 1 },
                        },
                        (metadata) => {
                            pb.task.setProgress(metadata.percent);
                        }
                    );
                    success = await this.importZip(singularType.toLowerCase(), name, blob, pkg.needsMigration, pb.task);
                }
                if (success) {
                    successes += 1;
                    pb.overall.successes += 1;
                } else {
                    failures += 1;
                    pb.overall.failures += 1;
                }

                if (this.missingAssets[name]) {
                    this.missingAssets[name] = Array.from(this.missingAssets[name]);
                }
            }
            console.log("Done with", type);
            results[type] = {
                successes,
                failures,
                installedFromBazaar,
                alerts: this.failedInstallReasons[type],
                missingAssets: this.missingAssets,
            };
        }

        return results;
    }

    async installPackage(type, name, version) {
        if (!["modules", "systems"].includes(type)) {
            return false;
        }
        if (name === "plutonium") {
            return false;
        }
        const response = await Utils.api(`data/${type}/install`, { name, version });
        if (!response) {
            return false;
        }
        return response.installed;
    }

    async importZip(type, name, blob, needsMigration, progressBar) {
        const formData = new FormData();
        formData.append("type", type);
        formData.append("zip", blob);
        if (needsMigration) {
            // FormData only supports strings, so let's not add the boolean if it's false
            formData.append("migrate", String(needsMigration));
        }
        const uploadProgress = (step, percent) => {
            if (step === 1) {
                progressBar.setProgress(percent * 100);
            }
        };
        const handleMigration = (status) => {
            if (!progressBar) {
                return;
            }
            progressBar.setLabel(t(`Migrating {{type}}`, { type }));
            switch (status) {
                case "pending":
                    progressBar.setProgress(0);
                    break;
                case "processing":
                    progressBar.setProgress(50);
                    break;
                case "done":
                    progressBar.setProgress(100);
                    break;
                default:
            }
        };
        let response = {};
        if (type === "world") {
            const idle = await Utils.api(`game/idle`, {});
            if (!idle.success) {
                progressBar.setAlert("warning", t(`You have a game currently in use. World imports may fail`));
            }
        }
        response = await Utils.apiTask(
            "data/import",
            formData,
            { progress: uploadProgress, json: false },
            { status: handleMigration }
        );
        if (type === "world" && response.code === 409 && response.secret) {
            const secret = response.secret;
            const data = { secret, name, inProgress: false };
            const query = $(`
            <table-tool-delete-query
                :value="true"
                :secret="secret"
                :name="name"
                :in-progress="inProgress"
                type="World"
                verb="${t("Overwrite")}"
                placeholder="${t("Enter overwrite code:")} ${secret}"
                title="${t("World already exists")}"
            >
                <div>${t("Could not import the world {{- name}}. A world with the same name already exists.", {
                    name: `<strong>'${Utils.escapeHTML(name)}'</strong>`,
                })}</div>
                <div>${t(
                    "If you wish to continue and overwrite it with the new data, enter the following code in the entry below {{- secret}}.",
                    { secret: `<strong>${secret}</strong>` }
                )}</div>
                <div class="mt-2 mb-3">${t(
                    "Press Cancel to keep the existing world untouched and return to the Import Wizard."
                )}</div>
            </table-tool-delete-query>`);
            const vm = await UI.renderVue(null, query[0], { data });
            const userSecret = await new Promise((resolve) => {
                vm.$children[0].$on("input", (val) => {
                    console.log("input : ", val);
                    if (!val) {
                        resolve(null);
                    }
                });
                vm.$children[0].$on("delete", (val) => {
                    console.log("delete : ", val);
                    data.inProgress = true;
                    resolve(val);
                });
            });
            data.inProgress = false;
            vm.$destroy();
            if (userSecret) {
                formData.append("overwrite", userSecret);
                response = await Utils.apiTask(
                    "data/import",
                    formData,
                    { progress: uploadProgress, json: false },
                    { status: handleMigration }
                );
            }
        }

        if (response.error) {
            const alert = {
                variant: "danger",
                message: t(`Error importing {{type}}: {{- error}}`, { type, error: response.error }),
            };
            const tp = `${type}s`;
            this.failedInstallReasons[tp] = this.failedInstallReasons[tp] || [];
            this.failedInstallReasons[tp].push(alert);
            progressBar.setAlert(alert.variant, alert.message);
            console.warn(`Error importing ${type}: ${response.error}`);
        }
        return !response.error && response.success;
    }

    async _importAssets(assets, progressBar, callback) {
        if (!this.remoteAssets) {
            progressBar.setLabel(t("Retrieving asset list for optimized upload"));
            this.remoteAssets = await Assets.getFiles();
        }
        const toUpload = assets.filter((a) => !a.oversize);
        const blobs = await Promise.all(
            toUpload.map(async (pkg) => {
                const blob = await pkg.file.blob;
                blob.hash = pkg.hash;
                blob.assetPath = pkg.path;
                if (!blob.name) {
                    blob.name = pkg.file.name;
                }
                return blob;
            })
        );
        const response = await Assets.uploadFiles(
            "",
            blobs,
            (type, progress, data, data2) => {
                if (type === "hash") {
                    const filename = data.includes("/") ? Utils.basename(data) : data;
                    progressBar.setLabel(t("Processing {{- filename}}", { filename }));
                } else if (type === "upload") {
                    const filename = data.includes("/") ? Utils.basename(data) : data;
                    progressBar.setLabel(t("Uploading {{- filename}}", { filename }));
                } else if (type === "files") {
                    const successes = data;
                    const failures = data2;
                    callback(successes, failures);
                }
                return true;
            },
            this.remoteAssets
        );
        return response;
    }

    async fetchRemoteAssets() {
        if (!this.remoteAssets) {
            this.remoteAssets = await Assets.getFiles();
        }
        if (!this.remoteAssetsObj) {
            this.remoteAssetsObj = this.remoteAssets.reduce((obj, asset) => {
                obj[asset.name] = asset;
                return obj;
            }, {});
        }
        return this.remoteAssetsObj;
    }

    findRemoteAsset(path) {
        return this.remoteAssetsObj[path] || this.remoteAssetsObj[decodeURIComponent(path)];
    }

    async migrateWorldData(world, progressBar) {
        progressBar.setLabel(t("Retrieving asset list for world migration"));
        await this.fetchRemoteAssets();
        progressBar.setLabel(t("Migrating World Data"));
        const zip_file = new JSZip();
        zip_file.file(`${world.path}${world.path ? "/" : ""}scenes/thumbs/`, null, { dir: true });
        const manifest = world.json || {};
        const packs = (manifest.packs || []).map((pack) => {
            const path = pack.path || `./packs/${pack.name}`;
            const cleanPath = path.replace(/^\/|^\.\//, "").replace(/\/+/g, "/");
            return { path: cleanPath, type: pack.type || pack.entity };
        });

        let i = 0;
        let migrated = 0;
        for (const file of world.files) {
            const relativePath = world.path === "" ? file.path : file.path.slice(world.path.length + 1);
            progressBar.setProgress(Math.round((100 * i) / world.files.length));
            i++;
            if (relativePath === "world.json") {
                const json = await Utils.blobToJson(await file.blob);
                if (json) {
                    json.background = this._migrateEntityPath(json.background);
                    zip_file.file(file.path, JSON.stringify(json, null, 2));
                    continue;
                }
            } else if (["data/users/CURRENT", "data/journal/CURRENT", "data/scenes/CURRENT"].includes(relativePath)) {
                // Test for v11+ with levelsdb database by checking for some specific databases
                world.needsMigration = true;
            } else if (
                [
                    "data/actors.db",
                    "data/cards.db",
                    "data/chat.db",
                    "data/messages.db",
                    "data/items.db",
                    "data/journal.db",
                    "data/macros.db",
                    "data/playlists.db",
                    "data/settings.db",
                    "data/scenes.db",
                    "data/tables.db",
                    "data/users.db",
                ].includes(relativePath)
            ) {
                const type = Utils.basename(relativePath).slice(0, -3);
                const data = await this._migrateDatabase(file, type);
                if (data) {
                    zip_file.file(file.path, data);
                    migrated++;
                    continue;
                }
            } else if (packs.some((pack) => `${pack.path}/CURRENT` === relativePath)) {
                // Test for v11+ with levelsdb database by checking for levelsdb format
                world.needsMigration = true;
            } else if (packs.some((pack) => pack.path === relativePath)) {
                const { type } = packs.find((pack) => pack.path === relativePath);
                const data = await this._migrateDatabase(file, type);
                if (data) {
                    zip_file.file(file.path, data);
                    migrated++;
                    continue;
                }
            }
            if (this.zip) {
                zip_file.files[file.path] = file;
            } else {
                zip_file.file(file.path, file);
            }
        }
        progressBar.setProgress(100);
        if (migrated === 0 || packs.some((pack) => pack.path.endsWith("/"))) {
            world.needsMigration = true;
        }
        return zip_file;
    }

    async migratePackageData(pkg, progressBar) {
        progressBar.setLabel(t("Retrieving asset list for package migration"));
        await this.fetchRemoteAssets();
        progressBar.setLabel(t("Migrating package data"));
        const zip_file = new JSZip();
        const manifest = pkg.json || {};
        const packs = (manifest.packs || []).map((pack) => {
            const path = pack.path || `./packs/${pack.name}`;
            const cleanPath = path.replace(/^\/|^\.\//, "").replace(/\/+/g, "/");
            return { path: cleanPath, type: pack.type || pack.entity };
        });

        let i = 0;
        let migrated = 0;
        for (const file of pkg.files) {
            const relativePath = pkg.path === "" ? file.path : file.path.slice(pkg.path.length + 1);
            progressBar.setProgress(Math.round((100 * i) / pkg.files.length));
            i++;
            if (packs.some((pack) => `${pack.path || `./packs/${pack.name}`}/CURRENT` === relativePath)) {
                // Test for v11+ with levelsdb database by checking for levelsdb format
                pkg.needsMigration = true;
            } else if (packs.some((pack) => (pack.path || `./packs/${pack.name}`) === relativePath)) {
                const { type } = packs.find((pack) => (pack.path || `./packs/${pack.name}`) === relativePath);
                const data = await this._migrateDatabase(file, type);
                if (data) {
                    zip_file.file(file.path, data);
                    migrated++;
                    continue;
                }
            }
            if (this.zip) {
                zip_file.files[file.path] = file;
            } else {
                zip_file.file(file.path, file);
            }
        }
        if (migrated === 0 || packs.some((pack) => (pack.path || `./packs/${pack.name}`).endsWith("/"))) {
            pkg.needsMigration = true;
        }
        progressBar.setProgress(100);
        return zip_file;
    }

    async _migrateDatabase(file, type) {
        const database = await Utils.blobToString(await file.blob);
        if (!database) {
            return null;
        }
        const lines = database.split("\n").map((line) => {
            if (!line) {
                return line;
            }
            try {
                const json = JSON.parse(line);
                const migrated = this._migrateEntity(type, json);
                return JSON.stringify(migrated);
            } catch (err) {
                console.error(err);
                return line;
            }
        });
        return lines.join("\n");
    }

    _migrateEntity(type, data) {
        switch (type) {
            case "Actor":
            case "actors":
                data.img = this._migrateEntityPath(data.img);
                if (data.prototypeToken) {
                    data.prototypeToken = this._migrateEntity("tokens", data.prototypeToken);
                } else if (data.token) {
                    data.token = this._migrateEntity("tokens", data.token);
                }
                if (data.items) {
                    data.items = data.items.map((item) => this._migrateEntity("items", item));
                }
                if (data.effects) {
                    data.effects = data.effects.map((effect) => this._migrateEntity("effects", effect));
                }
                if (
                    data.data &&
                    data.data.details &&
                    data.data.details.biography &&
                    data.data.details.biography.value
                ) {
                    data.data.details.biography.value = this._migrateHTML(data.data.details.biography.value);
                }
                break;
            case "tokens":
                if (data.texture) {
                    data.texture.src = this._migrateEntityPath(data.texture.src);
                } else if (data.img) {
                    data.img = this._migrateEntityPath(data.img);
                }
                if (data.effects) {
                    data.effects = data.effects.map((effect) => this._migrateEntityPath(effect));
                }
                if (data.actor) {
                    data.actor = this._migrateEntity("actors", data.actor);
                } else if (data.actorData) {
                    data.actorData = this._migrateEntity("actors", data.actorData);
                }
                break;
            case "effects":
                data.icon = this._migrateEntityPath(data.icon);
                break;
            case "Adventure":
            case "adventures":
                data.img = this._migrateEntityPath(data.img);
                data.caption = this._migrateHTML(data.caption);
                data.description = this._migrateHTML(data.description);
                for (const type of [
                    "actors",
                    "combats",
                    "items",
                    "scenes",
                    "journal",
                    "tables",
                    "macros",
                    "cards",
                    "playlists" /* , 'folders' */,
                ]) {
                    data[type] = data[type].map((entity) => this._migrateEntity(type, entity));
                }
                break;
            case "JournalEntry":
            case "journal":
                if (data.pages) {
                    data.pages = data.pages.map((page) => this._migrateEntity("JournalEntryPage", page));
                } else {
                    data.img = this._migrateEntityPath(data.img);
                    data.content = this._migrateHTML(data.content);
                }
                break;
            case "JournalEntryPage":
                data.src = this._migrateEntityPath(data.src);
                data.text.content = this._migrateHTML(data.text.content);
                data.text.markdown = this._migrateMarkdown(data.text.markdown);
                break;
            case "Item":
            case "items":
                data.img = this._migrateEntityPath(data.img);
                if (data.system && data.system.description && data.system.description.value) {
                    data.system.description.value = this._migrateHTML(data.system.description.value);
                } else if (data.data && data.data.description && data.data.description.value) {
                    data.data.description.value = this._migrateHTML(data.data.description.value);
                }
                break;
            case "RollTable":
            case "tables":
                data.img = this._migrateEntityPath(data.img);
                data.results = data.results.map((result) => this._migrateEntity("RollTableResult", result));
                break;
            case "Macro":
            case "macros":
            case "RollTableResult":
                data.img = this._migrateEntityPath(data.img);
                break;
            case "tiles":
                if (data.texture) {
                    data.texture.src = this._migrateEntityPath(data.texture.src);
                } else {
                    data.img = this._migrateEntityPath(data.img);
                }
                break;
            case "chat":
            case "messages":
                data.sound = this._migrateEntityPath(data.sound);
                data.content = this._migrateHTML(data.content);
                break;
            case "Playlist":
            case "playlists":
                data.sounds = data.sounds.map((sound) => this._migrateEntity("sound", sound));
                break;
            case "sound":
                data.path = this._migrateEntityPath(data.path);
                break;
            case "Scene":
            case "scenes":
                if (data.background) {
                    data.background.src = this._migrateEntityPath(data.background.src);
                } else {
                    data.img = this._migrateEntityPath(data.img);
                }
                data.foreground = this._migrateEntityPath(data.foreground);
                data.thumb = this._migrateEntityPath(data.thumb);
                data.description = this._migrateHTML(data.description);
                if (data.drawings) {
                    data.drawings = data.drawings.map((drawing) => this._migrateEntity("drawings", drawing));
                }
                if (data.notes) {
                    data.notes = data.notes.map((note) => this._migrateEntity("notes", note));
                }
                if (data.templates) {
                    data.templates = data.templates.map((template) => this._migrateEntity("templates", template));
                }
                if (data.tiles) {
                    data.tiles = data.tiles.map((tile) => this._migrateEntity("tiles", tile));
                }
                if (data.tokens) {
                    data.tokens = data.tokens.map((token) => this._migrateEntity("tokens", token));
                }
                break;
            case "drawings":
            case "templates":
                data.texture = this._migrateEntityPath(data.texture);
                break;
            case "notes":
                if (data.texture) {
                    data.texture.src = this._migrateEntityPath(data.texture.src);
                } else {
                    data.icon = this._migrateEntityPath(data.icon);
                }
                break;
            case "users":
                data.avatar = this._migrateEntityPath(data.avatar);
                break;
            case "Cards":
            case "cards":
                data.img = this._migrateEntityPath(data.img);
                data.cards = data.cards.map((card) => this._migrateEntity("CardData", card));
                break;
            case "CardData":
                data.img = this._migrateEntityPath(data.img);
                data.back = this._migrateEntity("CardFace", data.back);
                data.faces = data.faces.map((face) => this._migrateEntity("CardFace", face));
                break;
            case "CardFace":
                data.img = this._migrateEntityPath(data.img);
                break;
            case "settings":
                break;
            default:
        }
        return data;
    }

    _migrateHTML(content) {
        if (!content) {
            return content;
        }
        return content.replace(/src=("[^"]+"|'[^']+')/gi, (match, source) => {
            const src = this._migrateEntityPath(source.slice(1, -1));
            return match.substr(0, 5) + src + match.substr(-1);
        });
    }

    _migrateMarkdown(content) {
        if (!content) {
            return content;
        }
        return this._migrateHTML(content).replace(/\[([^\]]*)\]\(([^)]+)\)/gi, (match, text, source) => {
            const src = this._migrateEntityPath(source).replace(/\(/g, "%28").replace(/\)/, "%29"); // escape parenthesis
            return `[${text}](${src})`;
        });
    }

    _migrateEntityPath(path) {
        if (!path || path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
            if (
                path &&
                path.startsWith(
                    "https://storage.bhs.cloud.ovh.net/v1/AUTH_75d6cfdbfe4345d99836dac09158dc94/forgevtt/assets/"
                )
            ) {
                return path.replace(
                    "https://storage.bhs.cloud.ovh.net/v1/AUTH_75d6cfdbfe4345d99836dac09158dc94/forgevtt/assets/",
                    "https://assets.forge-vtt.com/"
                );
            }
            return path;
        }
        path = path
            .replace(/^\/|^\.\//, "")
            .replace(/\/+/g, "/")
            .replace(/\?.*/, "");
        if (path.startsWith("systems/")) {
            return path;
        }
        const asset = this.findRemoteAsset(path);
        if (!asset) {
            const basename = Utils.basename(path);
            if (basename.includes("*")) {
                const dirname = `${Utils.dirname(path)}/`;
                const directory = this.findRemoteAsset(dirname);
                if (directory) {
                    // Use a remoteAssets.find instead of findRemoteAsset because we don't know the path
                    // and we need to find a file first and grab its url, so use the slower .find method in the wildcard case
                    const firstAsset = this.remoteAssets.find((a) => a.url && a.name.startsWith(directory.name));
                    if (firstAsset) {
                        return `${Utils.dirname(firstAsset.url)}/${basename}`;
                    }
                }
            }
            // If path doesn't start with known beginning URIs, flag it as missing
            if (!/^(?:modules|systems|icons|ui|sounds)\//.test(path)) {
                if (!(this.currentPackage in this.missingAssets)) {
                    this.missingAssets[this.currentPackage] = new Set();
                }
                this.missingAssets[this.currentPackage].add(path);
            }
            return path;
        }
        return asset.url;
    }

    /**
     * Util to get the size of a file in the importer
     * @param file
     * @returns {number}
     * @private
     */
    _getFileSize(file) {
        let fileSize = 0;
        if (typeof file.size !== "undefined" && file.size !== null) {
            fileSize = file.size;
        } else if (
            file._data &&
            typeof file._data.uncompressedSize !== "undefined" &&
            file._data.uncompressedSize !== null
        ) {
            fileSize = file._data.uncompressedSize;
        }
        return fileSize;
    }
}
